// @flow

import PropTypes from 'prop-types';
import * as React from 'react';
import Rating from 'common/components/Rating';
import VenuePreviewPrice from './VenuePreviewPrice';
import PreviewImages from './searchResults/PreviewImages';
import SearchActionCreators from 'search/actions/SearchActionCreators';
import EditRelevanceLink, {
  editRelevanceLinkWrapperClass
} from './EditRelevanceLink';
import VenueLink from 'common/components/venueCard/VenueLink';
import cx from 'classnames';
import { type VenueResult } from 'search/types';

type Props = {
  highlightCapacitySitting: ?boolean,
  openInNewTab: boolean,
  venue: VenueResult,
  isActive: ?boolean,
  features: { edit_relevance?: boolean, ... },
  onClickVenueLink?: () => void,
  ...
};

class VenuePreview extends React.Component<Props> {
  static propTypes = {
    highlightCapacitySitting: PropTypes.bool,
    openInNewTab: PropTypes.bool,
    venue: PropTypes.object.isRequired,
    isActive: PropTypes.bool,
    features: PropTypes.shape({
      edit_relevance: PropTypes.bool
    }).isRequired,
    onClickVenueLink: PropTypes.func
  };

  static defaultProps: {|
    highlightCapacitySitting: boolean,
    openInNewTab: boolean
  |} = {
    highlightCapacitySitting: true,
    openInNewTab: true
  };

  render(): React.Node {
    const { venue, features, openInNewTab, onClickVenueLink } = this.props;

    // Capacity highlighting
    const capacityClasses = {
      'b-search-result__details-item': true,
      'b-search-result__details-item--with-separator': true
    };
    const capacitySittingClasses = cx(
      Object.assign({}, capacityClasses, {
        'is-disabled': !this.props.highlightCapacitySitting,
        'js-capacity-sitting': true // TODO: remove, only used in tests
      })
    );
    const capacityStandingClasses = cx(
      Object.assign({}, capacityClasses, {
        'is-disabled': this.props.highlightCapacitySitting,
        'js-capacity-standing': true // TODO: remove, only used in tests
      })
    );
    const previewBoxClass = cx({
      'b-search-result': true,
      'is-active': this.props.isActive
    });
    return (
      <li
        onFocus={this._handleFocus}
        onMouseEnter={this._handleFocus}
        className={editRelevanceLinkWrapperClass}
      >
        <div className={previewBoxClass}>
          <VenuePreviewImageArea
            venue={venue}
            openInNewTab={openInNewTab}
            onClickVenueLink={onClickVenueLink}
          />
          <VenuePreviewDetailsArea
            capacityClasses={capacityClasses}
            capacitySittingClasses={capacitySittingClasses}
            capacityStandingClasses={capacityStandingClasses}
            venue={venue}
            openInNewTab={openInNewTab}
            onClickVenueLink={onClickVenueLink}
          />
        </div>
        {features.edit_relevance && <EditRelevanceLink venueId={venue.id} />}
      </li>
    );
  }

  _handleFocus: () => void = () => {
    SearchActionCreators.focusSearchResult(this.props.venue.id);
  };
}

function VenuePreviewImageArea({ venue, openInNewTab, onClickVenueLink }) {
  return (
    <div className="b-overlay-container b-search-result__image-container">
      <PreviewImages
        venue={venue}
        openInNewTab={openInNewTab}
        onClickVenueLink={onClickVenueLink}
      />
      <VenuePreviewPrice venue={venue} />
      <div className="b-overlay b-overlay--right b-search-result__overlay">
        <span className="b-search-result__city">{venue.city}</span>
      </div>
    </div>
  );
}

function VenuePreviewDetailsArea({
  venue,
  capacityClasses,
  capacitySittingClasses,
  capacityStandingClasses,
  openInNewTab,
  onClickVenueLink
}) {
  return (
    <VenueLink
      venue={venue}
      openInNewTab={openInNewTab}
      className="b-search-result__info"
      onClickVenueLink={onClickVenueLink}
    >
      <h2
        className="b-search-result__name"
        title={venue.title}
        data-testid="search-result-name"
      >
        {!venue.published && <UnpublishedVenueWarning />}
        {venue.restricted && <RestrictedVenueWarning />}
        {venue.title}
      </h2>
      <span className="b-search-result__details b-search-result__details--left">
        <span className="b-search-result__details-item">
          <Rating score={venue.reviews_average} />
        </span>
        {venue.reviews_count > 0 && (
          <span className="b-search-result__details-item">
            <i className="fa fa-comment-o" />
            &nbsp;
            {venue.reviews_count}
          </span>
        )}
      </span>
      <span className="b-search-result__details b-search-result__details--right">
        <AvailabilityData
          hasAvailabilityData={venue.has_availability_data}
          className={cx(capacityClasses)}
        />
        <CapacitySittingData
          capacity={venue.capacity_sitting}
          className={capacitySittingClasses}
        />
        <CapacityStandingData
          capacity={venue.capacity_standing}
          className={capacityStandingClasses}
        />
      </span>
    </VenueLink>
  );
}

function UnpublishedVenueWarning() {
  return (
    <span>
      <i
        className="fa fa-exclamation-triangle m-icon-warning"
        title={__('search.venue_preview.published')}
      />{' '}
    </span>
  );
}

function RestrictedVenueWarning() {
  return (
    <span>
      <i
        className="fa fa-eye m-icon-warning"
        title={__('search.venue_preview.restricted')}
      />{' '}
    </span>
  );
}

function AvailabilityData({ className, hasAvailabilityData }) {
  if (!hasAvailabilityData) return null;

  return (
    <span
      className={className}
      title={__('search.venue_preview.has_availability_data')}
    >
      <i className="fa fa-calendar-check-o" />
    </span>
  );
}

function CapacitySittingData({ className, capacity }) {
  return (
    <span
      className={className}
      title={__('search.venue_preview.capacity_sitting')}
    >
      <i className="m-icon-sitting" />
      &nbsp;
      {capacity}
    </span>
  );
}

function CapacityStandingData({ className, capacity }) {
  return (
    <span
      className={className}
      title={__('search.venue_preview.capacity_standing')}
    >
      <i className="fa fa-male" />
      &nbsp;
      {capacity}
    </span>
  );
}

export default VenuePreview;
